import { ErrorOutline } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ApprovalPinModal from "src/components/DocViewer/components/docInfoDrawer/approversTab/pin/ApprovalPinModal";
import { useSignTrainingTask } from "src/hooks/queries/task-state-mutations";
import {
  useGetTraineeTrainingQuiz,
  useSubmitTrainingQuiz,
} from "src/hooks/queries/training-quiz";
import type { User } from "src/stores/models";

type TrainingQuizStepProps = {
  orgId: string;
  taskStateId: string;
  user: User;
  onComplete: () => void;
  onReviewDocument: () => void;
};

export const TrainingQuizStep = ({
  orgId,
  taskStateId,
  user,
  onComplete,
  onReviewDocument,
}: TrainingQuizStepProps) => {
  const quizQuery = useGetTraineeTrainingQuiz({ orgId, taskStateId });
  const submitQuiz = useSubmitTrainingQuiz({ orgId });
  const signTraining = useSignTrainingTask({ orgId });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [passed, setPassed] = useState(false);
  const [failedModalOpen, setFailedModalOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);

  useEffect(() => {
    setPassed(quizQuery.data?.passed === true);
  }, [quizQuery.data?.passed]);

  const quiz = quizQuery.data;
  const allAnswered =
    !!quiz && quiz.questions.every((question) => !!answers[question.id]);

  const handleSubmit = async () => {
    if (!quiz || !allAnswered) return;
    const result = await submitQuiz.mutateAsync({
      taskStateId,
      answers: quiz.questions.map((question) => ({
        questionId: question.id,
        optionId: answers[question.id],
      })),
    });

    if (result.passed) {
      setPassed(true);
      setPinModalOpen(true);
      return;
    }

    setFailedModalOpen(true);
  };

  const handleSign = async (pin?: string) => {
    if (!pin) return;
    await signTraining.mutateAsync({ stateId: taskStateId, pin });
    setPinModalOpen(false);
    onComplete();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          minHeight: 0,
          flex: 1,
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2 }}>
          {quizQuery.isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {quizQuery.isError && (
            <Alert severity="error">
              This quiz is not available. Confirm that you have marked the
              document as read and that the quiz is published.
            </Alert>
          )}

          {quiz && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6">Check your understanding</Typography>
                <Typography variant="body2" color="text.secondary">
                  Answer every question correctly, then sign the training with
                  your PIN.
                </Typography>
              </Box>

              {passed && (
                <Alert severity="success">
                  Quiz passed. Sign the training to complete it.
                </Alert>
              )}

              {!passed &&
                quiz.questions.map((question, questionIndex) => (
                  <FormControl key={question.id} component="fieldset">
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {questionIndex + 1}. {question.text}
                    </Typography>
                    <RadioGroup
                      value={answers[question.id] ?? ""}
                      onChange={(event) => {
                        setAnswers((current) => ({
                          ...current,
                          [question.id]: event.target.value,
                        }));
                      }}
                    >
                      {question.options.map((option) => (
                        <FormControlLabel
                          key={option.id}
                          value={option.id}
                          control={<Radio />}
                          label={option.text}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                ))}
            </Stack>
          )}
        </Box>

        {quiz && (
          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              px: 3,
              py: 2,
            }}
          >
            {passed ? (
              <Button
                fullWidth
                variant="contained"
                onClick={() => setPinModalOpen(true)}
                disabled={signTraining.isPending}
              >
                Sign training
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={!allAnswered || submitQuiz.isPending}
                startIcon={
                  submitQuiz.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {submitQuiz.isPending ? "Submitting…" : "Submit quiz"}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={failedModalOpen}
        onClose={() => setFailedModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "error.main",
          }}
        >
          <ErrorOutline color="error" />
          Quiz Attempt Failed
        </DialogTitle>
        <DialogContent>
          <Typography>
            Not all answers were correct. Review the document and try again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailedModalOpen(false)}>Try again</Button>
          <Button
            variant="contained"
            onClick={() => {
              setFailedModalOpen(false);
              onReviewDocument();
            }}
          >
            Review document
          </Button>
        </DialogActions>
      </Dialog>

      <ApprovalPinModal
        isOpen={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        onApprove={handleSign}
        user={user}
        title="Sign Training"
        description="Enter your 4-digit PIN to certify that you passed the quiz and understood the linked document."
        saveText="Sign"
        confirmationWarningText="By pressing Sign below, you certify that you passed the quiz and understood the linked document."
      />
    </>
  );
};
